package com.writerslab

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Domain model for static-analysis output, decoupled from the (now removed)
 * ResultsArea UI. The API-side counterparts live in ApiModels.kt and are
 * mapped into [AnalysisResult] via `ApiAnalysisResponse.toAnalysisResult()`.
 *
 * `Metric` is `@Serializable` so the API can ship reference-value maps
 * keyed directly by enum, e.g. `references: { "complexity": 5.14, ... }`
 * deserialises into `Map<Metric, Double>`. The `@SerialName` per entry
 * matches the snake_case keys the analyser already uses; some UI enum
 * names are inverted readings of the underlying analyser metric (e.g.
 * `LIVELINESS` is high when "bureaucratese" is low) — those mappings live
 * here so we have one source of truth.
 */

@Serializable
enum class Metric {
    @SerialName("complexity")           COMPLEXITY,
    @SerialName("vocabulary")           VOCABULARY,
    @SerialName("pace")                 PACE,
    @SerialName("dialogue")             DIALOGUE,
    @SerialName("expressiveness")       EXPRESSIVENESS,
    @SerialName("coherence")            COHERENCE,
    @SerialName("arc_volatility")       ARC_VOLATILITY,
    @SerialName("vividness")            VIVIDNESS,
    @SerialName("pathos")               PATHOS,
    @SerialName("morality")             MORALITY,
    @SerialName("author_stance")        AUTHOR_STANCE,
    @SerialName("voices")               VOICES,
    @SerialName("fillers")              FILLERS,
    @SerialName("sensory")              SENSORY,
    @SerialName("cliches")              ORIGINALITY,
    @SerialName("indirect_actions")     DIRECTNESS,
    @SerialName("weak_verbs")           VERB_STRENGTH,
    @SerialName("intensifiers")         PRECISION,
    @SerialName("bureaucratese")        LIVELINESS,
    @SerialName("sentence_variety")     RHYTHM,
    @SerialName("syntactic_simplicity") CLEAN_SYNTAX,
    @SerialName("vocabulary_range")     CEFR_RANGE,
    @SerialName("rare_vocabulary")      RARE_VOCABULARY,
    @SerialName("register_shift")       REGISTER_SHIFT,
    @SerialName("vocabulary_age")       VOCABULARY_AGE,
    @SerialName("stage_directions")     STAGE_DIRECTIONS,
    @SerialName("no_repetition")        NO_REPETITION,
    @SerialName("direct_pov")           DIRECT_POV,
    @SerialName("phrasal_verbs")        PHRASAL_VERBS,
    @SerialName("no_nominalisation")    NO_NOMINALISATION,
    @SerialName("concreteness")         CONCRETENESS,
}

data class MetricItem(
    val id: Metric,
    val value: Int,
    /** Population mean for this metric on the same 1-10 scale, when the
     *  language has a calibrated reference. `null` for metrics not in
     *  the language's quality formula. */
    val reference: Double? = null,
    /** Signed contribution of this metric to the overall quality z-score
     *  (positive = boosting, negative = dragging). `null` when the
     *  metric isn't part of the language's formula. */
    val contribution: Double? = null,
)

data class AnalysisResult(
    val words: Int,
    val chars: Int = 0,
    val quality: Int?,
    val language: Language?,
    val pov: POV?,
    val charactersCount: Int,
    val mood: Mood?,
    val emotionPcts: Map<Emotion, Double>,
    val tags: List<Tag>,
    val profile: List<MetricItem>,
    val character: List<MetricItem>,
    val style: List<MetricItem>,
    val styleReliable: Boolean,
    val metricsReliable: Boolean = true,
    /** Per-metric signed contribution to the overall quality z-score,
     *  computed server-side. Empty when the language has no calibrated
     *  quality model. See `findQualityDrags`. */
    val qualityContributions: Map<Metric, Double> = emptyMap(),
    /** Per-metric "good direction" — `"high"` / `"low"` / `"none"` —
     *  derived from LR coefficient sign × display polarity. Per-language
     *  (RU/EN have different mass-market preferences). Drives bar fill
     *  colour and drag-flag eligibility. */
    val qualityDirections: Map<Metric, String> = emptyMap(),
    /** When the text was last edited in the reader (ISO); null = as uploaded. */
    val textEditedAt: String? = null,
)

// Mirrors `Pricing.analysisCost` on the backend: ceiling-division of
// chars by `charsPerCredit`, minimum 1. Always pass
// `LocalPricing.current.analysisCharsPerCredit` so the UI never
// disagrees with what the server actually charges.
fun calcAiCost(chars: Int, charsPerCredit: Int): Int =
    maxOf(1, (chars + charsPerCredit - 1) / charsPerCredit)
